import React, { useRef, useEffect, useMemo } from 'react';
import '../styles/searchSuggestions.css';

const SearchSuggestions = ({
  suggestions = [],
  history = [],
  onSelect,
  isLoading = false,
  query = '',
  onClose = () => {},
  activeIndex = -1,
}) => {
  const suggestionsRef = useRef(null);

  // Filter and prioritize suggestions
  const filteredSuggestions = useMemo(() => {
    if (!query || query.trim().length < 1) {
      return history.slice(0, 5).map(item => typeof item === 'string' ? item : item.keyword);
    }

    const exactMatchFound = suggestions.some(
      suggestion => suggestion.toLowerCase() === query.toLowerCase()
    );

    let result = [...suggestions];
    
    if (!exactMatchFound && query.trim().length >= 2) {
      result.unshift(query);
    }

    return result.slice(0, 8);
  }, [suggestions, history, query]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && suggestionsRef.current) {
      const activeItem = suggestionsRef.current.querySelector('.suggestion-item.active');
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [activeIndex]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if ((!filteredSuggestions || filteredSuggestions.length === 0) && !isLoading) {
    return null;
  }

  let itemIndex = 0;

  return (
    <div className="search-suggestions" ref={suggestionsRef} id="search-suggestions-list" role="listbox">
      {isLoading ? (
        <div className="suggestions-loading">
          <div className="suggestions-loading-spinner"></div>
          <span>Recherche en cours...</span>
        </div>
      ) : (
        <>
          {query.trim().length === 0 && history.length > 0 && (
            <div className="suggestions-section">
              <div className="suggestions-section-header">
                <span>Recherches récentes</span>
              </div>
              {history.slice(0, 5).map((item, index) => {
                const currentIdx = itemIndex++;
                return (
                  <div
                    key={`history-${index}`}
                    id={`suggestion-${currentIdx}`}
                    role="option"
                    aria-selected={activeIndex === currentIdx}
                    className={`suggestion-item history-item ${activeIndex === currentIdx ? 'active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(typeof item === 'string' ? item : item.keyword, 'history');
                    }}
                  >
                    <i className="fas fa-history suggestion-icon"></i>
                    <span className="suggestion-text">{typeof item === 'string' ? item : item.keyword}</span>
                  </div>
                );
              })}
            </div>
          )}

          {query.trim().length > 0 && filteredSuggestions.length > 0 && (
            <div className="suggestions-section">
              <div className="suggestions-section-header">
                <span>Suggestions</span>
              </div>
              {filteredSuggestions.map((suggestion, index) => {
                const currentIdx = itemIndex++;
                const lowerSuggestion = suggestion.toLowerCase();
                const lowerQuery = query.toLowerCase();
                const startIndex = lowerSuggestion.indexOf(lowerQuery);
                
                return (
                  <div
                    key={`suggestion-${index}`}
                    id={`suggestion-${currentIdx}`}
                    role="option"
                    aria-selected={activeIndex === currentIdx}
                    className={`suggestion-item ${activeIndex === currentIdx ? 'active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(suggestion, 'suggestion');
                    }}
                  >
                    <i className="fas fa-search suggestion-icon"></i>
                    <span className="suggestion-text">
                      {startIndex !== -1 ? (
                        <>
                          {suggestion.substring(0, startIndex)}
                          <strong className="highlight">
                            {suggestion.substring(startIndex, startIndex + query.length)}
                          </strong>
                          {suggestion.substring(startIndex + query.length)}
                        </>
                      ) : (
                        suggestion
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchSuggestions;
